import React, { useState, useCallback, useEffect } from "react";
import { useAccount, useSignTypedData } from "wagmi";
import { ethers } from "ethers";
import {
  Heading,
  HeadingVariant,
  Text,
  TextVariant,
  TextWeight,
  View,
  Button,
  ButtonVariant,
  ButtonSize,
  CheckmarkIcon,
  InfoIcon,
  RefreshIcon,
  CopyIcon,
} from "@pufferfinance/puffer-ui-components";
import { CONTRACTS, X402_CONFIG } from "../constants/contracts";
import { UNIFI_CHAIN_ID, txExplorerUrl, AP2_SERVICE_URL } from "../constants/network";
import { getReadProvider } from "../utils/provider";
import { useAppToast } from "../hooks/useAppToast";
import styles from "./X402Payment.module.scss";

interface X402PaymentProps {
  onBalanceRefresh: () => void;
}

const truncate = (addr: string) =>
  addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : "";

export const X402Payment: React.FC<X402PaymentProps> = ({
  onBalanceRefresh,
}) => {
  const { showError, showSuccess, showInfo, showWalletError } = useAppToast();
  const { address, isConnected, connector } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();

  const [allowance, setAllowance] = useState<string>("0");
  const [isCheckingAllowance, setIsCheckingAllowance] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isPayment, setIsPayment] = useState(false);
  const [recurringIntent, setRecurringIntent] = useState<{
    user: string;
    merchant: string;
    token: string;
    amount: string;
    interval: number;
    expires: number;
  } | null>(null);
  const [recurringSignature, setRecurringSignature] = useState<string | null>(
    null,
  );

  const checkAllowance = useCallback(async () => {
    if (!address || !isConnected) return;

    try {
      setIsCheckingAllowance(true);
      const provider = getReadProvider();
      const usdcAbi = [
        "function allowance(address owner, address spender) view returns (uint256)",
      ];
      const usdcContract = new ethers.Contract(
        CONTRACTS.USDC,
        usdcAbi,
        provider,
      );

      const allowanceRaw = await usdcContract.allowance(
        address,
        CONTRACTS.PAYMENT_FACILITATOR,
      );
      const allowanceFormatted = ethers.formatUnits(allowanceRaw, 6);
      setAllowance(allowanceFormatted);
    } catch (error) {
      console.error("Error checking allowance:", error);
      showError("Failed to check USDC allowance");
    } finally {
      setIsCheckingAllowance(false);
    }
  }, [address, isConnected, showError]);

  const approveUSDC = useCallback(async () => {
    if (!address || !isConnected) {
      showError("Please connect your wallet");
      return;
    }

    if (!connector) {
      showError("No wallet connector active");
      return;
    }

    try {
      setIsApproving(true);
      // Get the EIP-1193 provider from the selected wagmi connector so we
      // target the wallet the user actually chose (not whichever one won
      // the window.ethereum race).
      const eip1193 = (await connector.getProvider()) as ethers.Eip1193Provider;
      const provider = new ethers.BrowserProvider(eip1193);
      const signer = await provider.getSigner();
      const usdcAbi = [
        "function approve(address spender, uint256 amount) returns (bool)",
      ];
      const usdcContract = new ethers.Contract(CONTRACTS.USDC, usdcAbi, signer);

      const approvalAmount = ethers.parseUnits("10", 6);

      let tx;
      try {
        tx = await usdcContract.approve(
          CONTRACTS.PAYMENT_FACILITATOR,
          approvalAmount,
        );
      } catch (gasError: any) {
        if (
          gasError.message?.includes("estimateGas") ||
          gasError.message?.includes("rate limit")
        ) {
          showInfo(
            "Gas estimation failed, retrying approval with manual gas limit...",
          );
          tx = await usdcContract.approve(
            CONTRACTS.PAYMENT_FACILITATOR,
            approvalAmount,
            { gasLimit: 100000 },
          );
        } else {
          throw gasError;
        }
      }

      showInfo("Approval transaction submitted. Waiting for confirmation...");

      await tx.wait();

      showSuccess(
        "USDC approval successful! You can now make x402 payments.",
      );
      await checkAllowance();
    } catch (error) {
      console.error("Approval failed:", error);
      showWalletError(error, "USDC approval");
    } finally {
      setIsApproving(false);
    }
  }, [
    address,
    isConnected,
    connector,
    showError,
    showInfo,
    showSuccess,
    showWalletError,
    checkAllowance,
  ]);

  const executePayment = useCallback(async () => {
    if (!address || !isConnected) {
      showError("Please connect your wallet");
      return;
    }

    if (parseFloat(allowance) < 0.1) {
      showError("Insufficient USDC allowance. Please approve USDC first.");
      return;
    }

    try {
      setIsPayment(true);

      if (!recurringIntent || !recurringSignature) {
        showInfo("Please sign the payment authorization...");

        const expires = Math.floor(Date.now() / 1000) + 24 * 60 * 60;

        const intent = {
          user: address,
          merchant: X402_CONFIG.MERCHANT_ADDRESS,
          token: CONTRACTS.USDC,
          amount: X402_CONFIG.PAYMENT_AMOUNT,
          interval: 0,
          expires,
        };

        const userSignature = await signTypedDataAsync({
          domain: {
            name: "PaymentFacilitator",
            version: "1",
            chainId: UNIFI_CHAIN_ID,
            verifyingContract: CONTRACTS.PAYMENT_FACILITATOR as `0x${string}`,
          },
          types: {
            RecurringIntentMandate: [
              { name: "user", type: "address" },
              { name: "merchant", type: "address" },
              { name: "token", type: "address" },
              { name: "amount", type: "uint256" },
              { name: "interval", type: "uint256" },
              { name: "expires", type: "uint256" },
            ],
          },
          primaryType: "RecurringIntentMandate",
          message: {
            user: intent.user as `0x${string}`,
            merchant: intent.merchant as `0x${string}`,
            token: intent.token as `0x${string}`,
            amount: BigInt(intent.amount),
            interval: BigInt(intent.interval),
            expires: BigInt(intent.expires),
          },
        });

        setRecurringIntent(intent);
        setRecurringSignature(userSignature);
        showSuccess(
          "Payment authorization signed! You can now pay multiple times.",
        );
        return;
      }

      showInfo("Executing payment via agent...");

      const res = await fetch(`${AP2_SERVICE_URL}/execute-recurring-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          recurringIntent,
          userSignature: recurringSignature,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || `Payment failed (${res.status})`);
      }

      const { txHash } = await res.json();

      showSuccess(
        `Successfully paid 0.1 USDC for ${X402_CONFIG.SERVICE_NAME} access (gasless)! View transaction: ${txExplorerUrl(txHash)}`,
      );
      await checkAllowance();
      onBalanceRefresh();
    } catch (error) {
      console.error("Payment failed:", error);
      const parsed = showWalletError(error, "Payment");
      if (parsed.isIntentExpired) {
        setRecurringIntent(null);
        setRecurringSignature(null);
      }
    } finally {
      setIsPayment(false);
    }
  }, [
    address,
    isConnected,
    allowance,
    recurringIntent,
    recurringSignature,
    signTypedDataAsync,
    showError,
    showSuccess,
    showInfo,
    showWalletError,
    checkAllowance,
    onBalanceRefresh,
  ]);

  useEffect(() => {
    if (isConnected && address) {
      checkAllowance();
    }
  }, [isConnected, address, checkAllowance]);

  const hasEnoughAllowance = parseFloat(allowance) >= 0.1;

  const copyMerchant = () => {
    navigator.clipboard
      .writeText(X402_CONFIG.MERCHANT_ADDRESS)
      .then(() => showInfo("Merchant address copied"))
      .catch(() => showError("Failed to copy address"));
  };

  return (
    <View flexDirection="column" gap={16} className={styles.wrapper}>
      {/* Header */}
      <View flexDirection="column" gap={2}>
        <Heading variant={HeadingVariant.Title3}>x402 Payment Protocol</Heading>
        <Text variant={TextVariant.Body2} color="var(--secondary-cadet-50)">
          Pay with USDC for API access - authorize once, pay many times.
        </Text>
      </View>

      {/* Service details */}
      <View flexDirection="column" gap={6} className={styles.detailsCard}>
        <View
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Text variant={TextVariant.Body2} color="var(--secondary-cadet-50)">
            Service
          </Text>
          <Text
            variant={TextVariant.Body2}
            weight={TextWeight.SemiBold}
            color="var(--primary-navy-90)"
          >
            {X402_CONFIG.SERVICE_NAME}
          </Text>
        </View>
        <View
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Text variant={TextVariant.Body2} color="var(--secondary-cadet-50)">
            Payment
          </Text>
          <Text
            variant={TextVariant.Body2}
            weight={TextWeight.SemiBold}
            color="var(--primary-navy-90)"
          >
            0.1 USDC / request
          </Text>
        </View>
        <View
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Text variant={TextVariant.Body2} color="var(--secondary-cadet-50)">
            Network
          </Text>
          <Text
            variant={TextVariant.Body2}
            weight={TextWeight.SemiBold}
            color="var(--primary-navy-90)"
          >
            Unifi Testnet
          </Text>
        </View>
        <View
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Text variant={TextVariant.Body2} color="var(--secondary-cadet-50)">
            Merchant
          </Text>
          <View flexDirection="row" gap={2} alignItems="center">
            <Text
              variant={TextVariant.Body2}
              weight={TextWeight.SemiBold}
              color="var(--primary-navy-90)"
              className={styles.mono}
            >
              {truncate(X402_CONFIG.MERCHANT_ADDRESS)}
            </Text>
            <Button
              variant={ButtonVariant.Unstyled}
              onPress={copyMerchant}
              aria-label="Copy merchant address"
              className={styles.iconButton}
            >
              <CopyIcon size={14} />
            </Button>
          </View>
        </View>
      </View>

      {/* Allowance status */}
      <View
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        className={
          hasEnoughAllowance ? styles.statusSuccess : styles.statusWarning
        }
      >
        <View flexDirection="row" gap={3} alignItems="center">
          <View className={styles.statusIcon}>
            {hasEnoughAllowance ? (
              <CheckmarkIcon size={16} />
            ) : (
              <InfoIcon size={16} />
            )}
          </View>
          <View flexDirection="column">
            <Text
              variant={TextVariant.Body2}
              weight={TextWeight.SemiBold}
              color="var(--primary-navy-90)"
            >
              USDC Allowance
            </Text>
            <Text
              variant={TextVariant.Caption1}
              color="var(--secondary-cadet-50)"
            >
              {isCheckingAllowance ? "Checking…" : `${allowance} USDC approved`}
            </Text>
          </View>
        </View>
        <Button
          variant={ButtonVariant.Ghost}
          size={ButtonSize.Small}
          onPress={checkAllowance}
          isDisabled={isCheckingAllowance}
          aria-label="Refresh allowance"
        >
          <RefreshIcon size={14} />
        </Button>
      </View>

      {/* Authorization status */}
      <View
        flexDirection="row"
        gap={3}
        alignItems="center"
        className={recurringIntent ? styles.statusInfo : styles.statusNeutral}
      >
        <View className={styles.statusIcon}>
          {recurringIntent ? (
            <CheckmarkIcon size={16} />
          ) : (
            <InfoIcon size={16} />
          )}
        </View>
        <View flexDirection="column">
          <Text
            variant={TextVariant.Body2}
            weight={TextWeight.SemiBold}
            color="var(--primary-navy-90)"
          >
            Payment Authorization
          </Text>
          <Text
            variant={TextVariant.Caption1}
            color="var(--secondary-cadet-50)"
          >
            {recurringIntent
              ? `Authorized until ${new Date(recurringIntent.expires * 1000).toLocaleString()}`
              : "Sign once to enable multiple payments"}
          </Text>
        </View>
      </View>

      {/* Actions */}
      {isConnected ? (
        <View flexDirection="row" gap={4} className={styles.actions}>
          {!hasEnoughAllowance && (
            <Button
              variant={ButtonVariant.Border}
              size={ButtonSize.Large}
              onPress={approveUSDC}
              isDisabled={isApproving}
              isPending={isApproving}
              flex={1}
              display="flex"
            >
              {isApproving ? "Approving…" : "Approve USDC"}
            </Button>
          )}
          <Button
            variant={ButtonVariant.CTA}
            size={ButtonSize.Large}
            onPress={executePayment}
            isDisabled={isPayment || !hasEnoughAllowance}
            isPending={isPayment}
            flex={1}
            display="flex"
          >
            {isPayment
              ? "Processing…"
              : recurringIntent
                ? "Pay 0.1 USDC"
                : "Authorize Payments"}
          </Button>
        </View>
      ) : (
        <View className={styles.connectHint}>
          <Text variant={TextVariant.Body2} color="var(--secondary-cadet-50)">
            Please connect your wallet to use x402 payments.
          </Text>
        </View>
      )}
    </View>
  );
};
